import React from "react";
import { MonitorProvider, useMonitor } from "./context/MonitorContext";
import MonitorHeader from "./components/MonitorHeader";
import UnifiedFlightFilter from "./components/UnifiedFlightFilter";
import FlightListView from "./components/FlightListView";
import CompactListView from "./components/CompactListView";
import FlightTimelineView from "./components/FlightTimelineView";
import FlightCalendarView from "./components/FlightCalendarView";
import FlightDetailDrawer from "./components/FlightDetailDrawer";
import EnhancedFlightTimesModal from "./components/EnhancedFlightTimesModal";
import { Card, FloatButton, Input, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { MODAL_KEYS, FLIGHT_STATUS, FLIGHT_STATUS_LABELS, FLIGHT_STATUS_COLORS, FLIGHT_STATUS_ICONS, DEFAULT_FILTER_STATUS } from "./constants";
import { VIEW_TYPE } from "./constants/viewConstants";
import CollapsibleFilterBar, { formatArraySummary } from "../../../components/common/CollapsibleFilterBar";
import AircraftSelector from "../../../components/common/AircraftSelector";
import agentGovernanceApi from "../../../api/v3/agentGovernanceApi";
import dayjs from "dayjs";

/**
 * 航班监控 - 正式功能页面
 *
 * 架构设计说明：
 *
 * 1. 项目架构遵循分层设计模式：
 *    - services/ 目录：包含所有业务逻辑服务，按功能领域划分
 *      - FlightService：处理航班数据的获取、格式化和分析逻辑
 *      - TransactionService：处理燃油申请、许可申请等事务管理逻辑
 *      - AiActionService：处理AI推荐动作项的获取、处理和分析逻辑
 *      - ViewService：处理不同视图（列表/日历/时间轴）的数据转换和显示逻辑
 *      - FilterService：处理筛选参数构建、有效性验证等逻辑
 *    - context/MonitorContext：全局状态管理，连接服务层和UI组件
 *    - hooks/useTransactions：提供事务管理的自定义Hook，内部调用TransactionService
 *    - components/：纯UI组件，通过useMonitor hook获取状态和方法
 *
 * 2. 主要数据流向：
 *    - 用户交互 -> 组件调用Context提供的方法 -> Context调用对应Service ->
 *      Service处理逻辑并返回结果 -> Context更新状态 -> 组件自动重新渲染
 *
 * 3. 主要业务规则：
 *    - 所有与API交互的代码集中在Services层，UI组件不直接调用API
 *    - 显示逻辑与数据处理逻辑分离，确保关注点分离
 *    - Context仅存储共享状态，不包含复杂业务逻辑
 *    - 服务层可被单独测试，提升代码的可维护性
 * */

// 默认状态集合（用于判断是否为非默认筛选）
const DEFAULT_STATUS_SET = new Set(DEFAULT_FILTER_STATUS);
const OPEN_AG_REVIEW_STATUSES = new Set(["open", "under_review"]);

function extractV3Data(response) {
    return response?.data?.data ?? response?.data ?? null;
}

function normalizeText(value, fallback = "") {
    if (value === null || value === undefined) return fallback;
    const text = String(value).replace(/\s+/g, " ").trim();
    return text || fallback;
}

function toArray(value) {
    return Array.isArray(value) ? value : [];
}

function isPendingReviewBinding(binding) {
    const status = normalizeText(binding?.review_case_summary?.status).toLowerCase();
    return OPEN_AG_REVIEW_STATUSES.has(status);
}

function buildReviewSignal(binding) {
    return {
        reviewCaseId: normalizeText(binding?.review_case_id || binding?.review_case_summary?.id),
        summary: normalizeText(binding?.review_case_summary?.summary, "存在一项邮件判断待审核"),
    };
}

function useSelectedFlightAgReviewState(selectedFlight) {
    const [state, setState] = React.useState({ loading: false, error: "", items: [] });

    React.useEffect(() => {
        let cancelled = false;

        async function loadAgReviewSignals() {
            if (!selectedFlight?.id) {
                setState({ loading: false, error: "", items: [] });
                return;
            }

            setState({ loading: true, error: "", items: [] });
            try {
                const response = await agentGovernanceApi.listEntityBindings({
                    entityType: "flight_operation",
                    entityIds: [selectedFlight.id],
                    bindingStatus: "all",
                    limit: 20,
                });
                const bindings = toArray(extractV3Data(response)?.bindings);
                const itemsByReviewCaseId = bindings
                    .filter(isPendingReviewBinding)
                    .map(buildReviewSignal)
                    .filter((item) => item.reviewCaseId)
                    .reduce((items, item) => {
                        if (!items.has(item.reviewCaseId)) items.set(item.reviewCaseId, item);
                        return items;
                    }, new Map());
                const items = Array.from(itemsByReviewCaseId.values());
                if (!cancelled) {
                    setState({ loading: false, error: "", items });
                }
            } catch (error) {
                console.error("加载航班 AG 审核信号失败", error);
                if (!cancelled) {
                    setState({ loading: false, error: error?.response?.status === 403 ? "" : "审核入口暂时无法加载", items: [] });
                }
            }
        }

        loadAgReviewSignals();

        return () => {
            cancelled = true;
        };
    }, [selectedFlight?.id]);

    return state;
}

/**
 * 航班监控筛选摘要计算
 * 将 MonitorContext 中的 filters 结构翻译为 CollapsibleFilterBar 的 summaryItems
 */
const useFlightFilterSummary = (filters) => {
    return React.useMemo(() => {
        const items = [];

        // 状态 —— hidden: 外层已有独立 Select 控件，标签不显示，但计入总数和 tooltip
        if (filters.status && filters.status.length > 0) {
            const allLabels = filters.status.map((s) => FLIGHT_STATUS_LABELS[s] || s);
            const fullValue = allLabels.join(" / ");

            if (filters.status.length === Object.keys(FLIGHT_STATUS).length) {
                items.push({ label: "状态", value: "全部", fullValue, hidden: true });
            } else {
                const isDefault = filters.status.length === DEFAULT_STATUS_SET.size
                    && filters.status.every((s) => DEFAULT_STATUS_SET.has(s));
                const value = formatArraySummary(filters.status, FLIGHT_STATUS_LABELS);
                items.push({
                    label: "状态",
                    value: isDefault ? `${value} (默认)` : value,
                    fullValue,
                    hidden: true,
                });
            }
        }

        // 日期范围
        if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
            const start = dayjs.utc(filters.dateRange[0]).format("MM-DD");
            const end = dayjs.utc(filters.dateRange[1]).format("MM-DD");
            items.push({ label: "日期", value: `${start} ~ ${end}` });
        }

        // 飞机注册号 —— hidden: 外层已有独立 AircraftSelector 控件
        if (filters.registrations && filters.registrations.length > 0) {
            const fullValue = filters.registrations.join(" / ");
            items.push({
                label: "飞机",
                value: formatArraySummary(filters.registrations, (v) => v, 3, "架"),
                fullValue,
                hidden: true,
            });
        }

        // 出发/到达机场
        if (filters.departure_airport) {
            items.push({ label: "DEP", value: filters.departure_airport });
        }
        if (filters.arrival_airport) {
            items.push({ label: "ARR", value: filters.arrival_airport });
        }

        // 搜索文本
        if (filters.searchText) {
            const display = filters.searchText.length > 10 ? filters.searchText.substring(0, 10) + "..." : filters.searchText;
            items.push({ label: "搜索", value: display, fullValue: filters.searchText });
        }

        return items;
    }, [filters]);
};

// 内部组件，用于访问 useMonitor 上下文
// 该组件必须在 MonitorProvider 内部使用
const MonitorContent = () => {
    // 使用monitor context提供的状态和方法
    const { activeView, modalVisible, toggleModal, isMobile, airportsMap, selectedFlight, flights, refreshFlightsByIds, filters, updateFilters, loading } = useMonitor();
    const agReviewState = useSelectedFlightAgReviewState(selectedFlight);

    // 筛选摘要
    const filterSummaryItems = useFlightFilterSummary(filters);

    // 快捷搜索（常驻在摘要栏）
    const [quickSearchValue, setQuickSearchValue] = React.useState(filters.searchText || "");
    const searchDebounceRef = React.useRef(null);

    // 同步 context filters 到本地搜索框
    React.useEffect(() => {
        setQuickSearchValue(filters.searchText || "");
    }, [filters.searchText]);

    const handleQuickSearchChange = React.useCallback((e) => {
        const value = e.target.value;
        setQuickSearchValue(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            updateFilters({ ...filters, searchText: value });
        }, 500);
    }, [filters, updateFilters]);

    const handleResetFilters = React.useCallback(() => {
        const today = dayjs.utc();
        updateFilters({
            status: DEFAULT_FILTER_STATUS,
            dateRange: [today.subtract(1, "day").format(), today.add(6, "day").format()],
            registrations: [],
            searchText: "",
            departure_airport: "",
            arrival_airport: "",
            transactionFilters: filters.transactionFilters, // 保留事务筛选
        });
        setQuickSearchValue("");
    }, [updateFilters, filters.transactionFilters]);

    // 处理航班信息编辑成功的回调
    const handleFlightInfoEditSuccess = React.useCallback(
        (flightId, responseData) => {
            const affectedFlightIds = Array.isArray(responseData?.affected_flight_ids) && responseData.affected_flight_ids.length > 0
                ? responseData.affected_flight_ids
                : [flightId];
            refreshFlightsByIds(affectedFlightIds, false);
        },
        [refreshFlightsByIds],
    );

    // 根据 activeView 渲染不同的视图组件
    const renderActiveView = () => {
        switch (activeView) {
            case VIEW_TYPE.COMPACT:
                return <CompactListView airportsMap={airportsMap} />;
            case VIEW_TYPE.TIMELINE:
                return <FlightTimelineView airportsMap={airportsMap} />;
            case VIEW_TYPE.CALENDAR:
                return <FlightCalendarView airportsMap={airportsMap} />;
            case VIEW_TYPE.LIST:
            default:
                return <FlightListView airportsMap={airportsMap} />;
        }
    };

    // 紧凑视图自带筛选组件，不需要外层FlightFilter
    const isCompactView = activeView === VIEW_TYPE.COMPACT;

    // 航班状态选项（带图标和颜色，与 UnifiedFlightFilter 一致）
    const statusOptions = React.useMemo(() =>
        Object.keys(FLIGHT_STATUS).map((key) => {
            const val = FLIGHT_STATUS[key];
            return {
                value: val,
                label: (
                    <span style={{ color: FLIGHT_STATUS_COLORS[val] || FLIGHT_STATUS_COLORS.default }}>
                        {FLIGHT_STATUS_ICONS[val] || FLIGHT_STATUS_ICONS.default}{' '}
                        {FLIGHT_STATUS_LABELS[val] || val}
                    </span>
                ),
                rawLabel: FLIGHT_STATUS_LABELS[val] || val,
            };
        }),
    []);

    return (
        <div className="page-container">
            {/* 航班监控头部 */}
            <MonitorHeader />

            {/* 搜索过滤 - 紧凑视图自带筛选，不显示外层 */}
            {!isCompactView && (
                <CollapsibleFilterBar
                    summaryItems={filterSummaryItems}
                    onReset={handleResetFilters}
                    drawerTitle="航班监控 - 筛选条件"
                    quickControls={
                        <>
                            <Select
                                mode="multiple"
                                placeholder="航班状态"
                                size="small"
                                maxTagCount={2}
                                maxTagTextLength={4}
                                value={filters.status}
                                onChange={(val) => updateFilters({ ...filters, status: val })}
                                optionFilterProp="rawLabel"
                                filterOption={(input, option) =>
                                    option && option.rawLabel && option.rawLabel.toLowerCase().includes(input.toLowerCase())
                                }
                                style={{ minWidth: 120, maxWidth: 220 }}
                                options={statusOptions}
                                allowClear
                            />
                            <AircraftSelector
                                mode="multiple"
                                placeholder="飞机"
                                size="small"
                                maxTagCount={2}
                                maxTagTextLength={6}
                                displayMode="simple"
                                popupMatchSelectWidth={false}
                                value={filters.registrations}
                                onChange={(val) => updateFilters({ ...filters, registrations: val || [] })}
                                style={{ minWidth: 100, maxWidth: 200 }}
                                allowClear
                            />
                            <Input
                                placeholder="注册号/航班号/机场/备注/航段(如HKG-NQZ)/STC"
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                size="small"
                                style={{ width: 380, height: 24 }}
                                value={quickSearchValue}
                                onChange={handleQuickSearchChange}
                                allowClear
                                onClear={() => {
                                    setQuickSearchValue("");
                                    updateFilters({ ...filters, searchText: "" });
                                }}
                            />
                        </>
                    }
                >
                    <UnifiedFlightFilter />
                </CollapsibleFilterBar>
            )}

            {/* 数据卡片 - 紧凑视图使用更简洁的样式 */}
            <Card className="data-card" style={isCompactView ? { padding: 0 } : undefined} styles={isCompactView ? { body: { padding: 0 } } : undefined}>
                {renderActiveView()}
            </Card>

            <FloatButton.BackTop />

            {/* 航班详情抽屉 */}
            <FlightDetailDrawer visible={modalVisible[MODAL_KEYS.FLIGHT_DETAIL]} onClose={() => toggleModal(MODAL_KEYS.FLIGHT_DETAIL, false)} agReviewState={agReviewState} />

            {/* 增强航班时间模态框 */}
            <EnhancedFlightTimesModal visible={modalVisible[MODAL_KEYS.ENHANCED_FLIGHT_TIMES]} onClose={() => toggleModal(MODAL_KEYS.ENHANCED_FLIGHT_TIMES, false)} flight={selectedFlight} airportsMap={airportsMap} allFlights={flights} onFlightInfoEditSuccess={handleFlightInfoEditSuccess} />
        </div>
    );
};

// 主页面组件
const FlightMonitorPage = () => {
    return (
        <MonitorProvider>
            <MonitorContent />
        </MonitorProvider>
    );
};

export default FlightMonitorPage;
